import { z } from "zod";

// ADV-923: External event envelope (delivered to Hookdeck Outpost as the
// `data` payload, and signed by Hookdeck on the way out).
//
// Shape is Standard Webhooks compatible — the envelope itself is a single
// JSON object; Standard Webhooks signing is applied to its serialized form
// by Hookdeck. The schema is versioned via `spec_version` so future breaking
// changes can ship without disrupting existing customers.
//
// Field provenance:
//   * id            — activity_event.id
//   * type          — activityEventTopic(event_type, entity_type)
//   * occurred_at   — activity_event.created_at (ISO 8601)
//   * account_id    — activity_event.account_id
//   * business_id   — activity_event.business_id (NULL for account-scoped events)
//   * data          — Debezium-shape diff from activity_event.metadata:
//                       { before?, after?, changed_fields? } for entity events,
//                       arbitrary system payload for import.* events
//   * origin        — activity_event.origin_type + origin_id (ADV-919)
//   * actor         — activity_event.actor_type + actor_id (ADV-918)
//   * spec_version  — literal "v1" — bump on breaking envelope changes

export const ExternalEventActorSchema = z.object({
  type: z.string().describe("Actor Type"),
  id: z.string().nullable().describe("Actor ID"),
});

export type ExternalEventActor = z.infer<typeof ExternalEventActorSchema>;

export const ExternalEventOriginSchema = z.object({
  type: z.string().nullable().describe("Origin Type"),
  id: z.string().nullable().describe("Origin ID"),
});

export type ExternalEventOrigin = z.infer<typeof ExternalEventOriginSchema>;

export const ExternalEventEnvelopeSchema = z.object({
  id: z.string().uuid().describe("Event ID"),
  type: z.string().describe("Event Type"),
  occurred_at: z.string().describe("Occurred At"),
  account_id: z.string().uuid().describe("Account"),
  business_id: z.string().uuid().nullable().describe("Business"),
  data: z.record(z.unknown()).describe("Data"),
  origin: ExternalEventOriginSchema.describe("Origin"),
  actor: ExternalEventActorSchema.describe("Actor"),
  spec_version: z.literal("v1").describe("Spec Version"),
});

export type ExternalEventEnvelope = z.infer<typeof ExternalEventEnvelopeSchema>;
